<template>
  <div class="avatar__container">
    <input
      type="file"
      class="d-none"
      ref="fileInput"
      accept=".jpg,.jpeg,.png,.webp"
      @change="setImage"
    />
    <div class="avatar" @click="openFileInput">
      <img
        :src="profilePicture || 'https://via.placeholder.com/200'"
        alt="avatar"
        class="avatar--img"
      />
      <div class="avatar--overlay">
        <v-icon>mdi-camera</v-icon>
      </div>
    </div>
    <v-dialog
      v-model="isEditing"
      max-width="70vw"
      persistent
      transition="scale-transition"
    >
      <div class="p-5 bg-white">
        <vue-cropper
          ref="cropper"
          :src="imgSrc"
          alt="Source Image"
          :aspectRatio="1 / 1"
          :initialAspectRatio="1 / 1"
          :viewMode="3"
          dragMode="none"
          :minCropBoxWidth="200"
          :minCropBoxHeight="200"
        ></vue-cropper>

        <div class="mt-5 w-full d-flex gap-4 justify-content-center">
          <v-btn
            color="error"
            width="100"
            @click="onCancel"
            :disabled="isUploading"
          >
            {{ $t("Cancel") }}
          </v-btn>
          <v-btn
            color="success"
            width="100"
            @click="onSave"
            :loading="isUploading"
            :disabled="isUploading"
          >
            {{ $t("save") }}
          </v-btn>
        </div>
      </div>
    </v-dialog>
  </div>
</template>

<script>
import VueCropper from "vue-cropperjs";
import "cropperjs/dist/cropper.css";
import { mapGetters, mapActions } from "vuex";
import GlobalMixin from "../GlobalMixin";

export default {
  name: "Avatar",
  mixins: [GlobalMixin],
  components: {
    VueCropper,
  },
  data() {
    return {
      profilePicture: null,
      imgSrc: null,
      isEditing: false,
      isUploading: false,
    };
  },

  mounted() {
    this.profilePicture = this.StateUser.user.profile_picture_url;
  },

  methods: {
    ...mapActions(["UpdateUser"]),

    /**
     * @method openFileInput
     * @description Opens the file input dialog when the avatar is clicked
     * @returns {void}
     */
    openFileInput() {
      this.$refs.fileInput.click();
    },

    /**
     * @method setImage
     * @description Sets the image for the vue-cropperjs library to be cropped
     * @param {event} event
     * @returns {void}
     */
    setImage(event) {
      const file = event.target.files[0];

      // If no file selected, return
      if (!file) return;

      if (file.type.indexOf("image/") === -1) {
        alert("Please select an image file");
        return;
      }

      if (typeof FileReader === "function") {
        const reader = new FileReader();

        reader.onload = (event) => {
          this.imgSrc = event.target.result;
          this.$refs.cropper.replace(event.target.result);
        };

        reader.readAsDataURL(file);
        this.isEditing = true;
      } else {
        alert("Sorry, FileReader API not supported");
      }
    },

    /**
     * @method onCancel
     * @description Cancels the image cropping process
     * @returns {void}
     */
    onCancel() {
      this.isEditing = false;
      this.isUploading = false;
      this.$refs.fileInput.value = "";
      this.$refs.cropper.destroy();
    },

    /**
     * @method onSave
     * @description Saves the cropped image
     * @returns {void}
     */
    onSave() {
      if (this.isUploading) return;

      this.isUploading = true;
      const canvas = this.$refs.cropper.getCroppedCanvas();
      const dataUrl = canvas.toDataURL("image/jpeg");
      this.profilePicture = dataUrl;
      canvas.toBlob((blob) => {
        const file = new File([blob], "avatar.jpg", { type: "image/jpeg" });
        // upload the image to the server
        this.uploadImage(file);
      });
    },

    /**
     * @method uploadImage
     * @description Uploads the image to API
     * @param {file} file
     * @returns {void}
     */
    uploadImage(file) {
      const formData = new FormData();
      formData.append("image_data", file);
      formData.append("user", this.StateUser.user.id);
      formData.append(
        "institute",
        this.StateUser.user.username.split("_").at(-1)
      );
      this.axios
        .post(
          `${process.env.VUE_APP_APIEndPoint}auth/upload_profile_picture/`,
          formData,
          this.headers
        )
        .then((res) => {
          const newUserObj = {
            ...this.StateUser.user,
            profile_picture_url: res.data.data.profile_picture_url,
          };
          this.UpdateUser(newUserObj);
        })
        .catch((err) => this.formatErrors(err))
        .finally(this.onCancel);
    },
  },
  computed: {
    ...mapGetters(["StateUser"]),
  },
};
</script>

<style lang="scss" scoped>
.avatar__container {
  display: grid;
  place-items: center;
  margin-bottom: 20px;
  .avatar {
    position: relative;
    width: 200px;
    height: 200px;
    cursor: pointer;

    &--img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      border-radius: 50%;
    }

    &--overlay {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.5);
      display: flex;
      justify-content: center;
      align-items: center;
      opacity: 0;
      transition: opacity 0.3s ease;
      border-radius: 50%;

      .mdi-camera::before {
        color: white;
        font-size: 1em;
      }
    }

    &:hover {
      .avatar--overlay {
        opacity: 1;
      }
    }
  }
}
</style>
